import { ProjectCardFragment } from '@/gql/generated/graphql'
import { AllPurposeLink } from '@components/all-purpose-link'
import { gridItemTransition } from '@components/grid'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'
import { projectPurchaseRoute, projectShowRoute } from '@utils/routes'
import { BodyText, DetailText } from 'design-configuration/components'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { motion } from 'motion/react'
import { useState } from 'react'
import { getProjectStatus, getVerticalTitle, ValidStatusType } from '@utils/project'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@components/button'
import { isSupportedMinterType } from '@artblocks/sdk/dist/machines/utils'

export const ProjectCard = ({
  project,
  dark = false
}: {
  project: ProjectCardFragment
  dark?: boolean
}): React.ReactElement => {
  const [opacity, setOpacity] = useState(0)
  const status = getProjectStatus(project)

  const token = project.featured_token?.[0] ?? project.oob_tokens?.[0]

  const isSupportedMinter = isSupportedMinterType(project.minter_configuration?.minter?.minter_type)
  // if (!token) return <></>
  return (
    <motion.div
      className="group relative flex w-full flex-col items-center"
      key={project.id}
      id={project.id}
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={gridItemTransition}
    >
      <AllPurposeLink
        href={projectShowRoute(project.slug)}
        onClick={() => {
          trackButtonClicked(TrackedButtonNames.projectCard, {
            project
          })
        }}
        className="relative flex w-full flex-col items-center justify-start text-center"
      >
        <motion.div
          className={`relative flex w-full ${dark ? `bg-surface-low-inverted` : `bg-surface-low`} aspect-square h-auto rounded-[2px]`}
          layout
          transition={{ duration: 0.3 }}
          animate={{ opacity: opacity }}
        >
          <motion.div
            className="relative h-full w-full"
            layout
            transition={{ duration: 0.3 }}
            animate={{ opacity: opacity }}
          >
            {status !== ValidStatusType.complete && status !== ValidStatusType.paused ? (
              <motion.div
                className="gap-xs bg-surface-container-inverted text-on-surface-inverted px-xs absolute left-0 top-0 flex items-center justify-center rounded-br-sm py-0.5"
                layout
                transition={{ duration: 0.3 }}
              >
                {status === ValidStatusType.live && (
                  <div className="h-2xs w-2xs rounded-full bg-[#03FD00] [filter:drop-shadow(0px_0px_4px_#03FD00)]" />
                )}
                <DetailText className="font-medium">{status}</DetailText>
              </motion.div>
            ) : null}
            <ImageWithCustomLoader
              onLoad={() => setOpacity(1)}
              src={token?.media_url ?? ''}
              className="p-md drop-shadow-[3px_3px_6px_rgba(0,0,0,0.20)] transition-all duration-300 hover:scale-[1.01] hover:drop-shadow-[3px_5px_7px_rgba(0,0,0,0.20)]"
              fill
              style={{
                objectFit: 'contain',
                objectPosition: 'center'
              }}
              alt={`Featured token for: ${project?.name}`}
              sizes="33vw"
            />
          </motion.div>
        </motion.div>
        <motion.div
          layout="position"
          transition={{ duration: 0.3 }}
          className="mt-md flex flex-col items-center justify-center"
        >
          <div className="gap-2xs flex flex-col items-center justify-center">
            <DetailText className="text-on-surface-variant font-medium uppercase">
              {getVerticalTitle(project)}
            </DetailText>
            <BodyText className="font-medium">{project.name}</BodyText>
            <BodyText>{project.artist_name}</BodyText>
          </div>
        </motion.div>
      </AllPurposeLink>
      {status === ValidStatusType.live && isSupportedMinter ? (
        <AllPurposeLink
          href={projectPurchaseRoute(project.slug)}
          className={cn(
            buttonVariants({
              colorTheme: 'outline'
            }),
            'mt-sm self-center opacity-0 transition-opacity duration-200 group-hover:opacity-100'
          )}
        >
          <BodyText asChild>
            <span>Purchase</span>
          </BodyText>
        </AllPurposeLink>
      ) : null}
    </motion.div>
  )
}
